<template>
    <div class="profile-update-page">
        <div class="header">
            <h2>Update Your Profile</h2>
            <div class="actions">
                <el-button @click="resetForm" size="small">Discard Changes</el-button>
                <el-button @click="updateUser" type="primary" size="small">Update Profile</el-button>
            </div>
        </div>
        <div class="content">
            <el-card class="form-card">
                <el-form :model="form" label-position="top">
                    <el-row :gutter="20">
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="First Name">
                                <el-input v-model="form.first_name"></el-input>
                            </el-form-item>
                        </el-col>
                        <el-col :xs="24" :sm="12">
                            <el-form-item label="Last Name">
                                <el-input v-model="form.last_name"></el-input>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-form-item label="Verified Phone Number">
                        <vue-tel-input v-model="form.phone" :default-country="'UN'"
                            :preferred-countries="['US', 'GB', 'CN']">
                        </vue-tel-input>
                    </el-form-item>

                    <el-form-item label="Email Address">
                        <el-input v-model="form.email" disabled></el-input>
                    </el-form-item>

                    <!-- 自定义头像上传与预览 -->
                    <el-form-item label="Avatar">
                        <!-- 上传部分：不显示内建文件列表，也不使用 picture-card，自定义预览 -->
                        <el-upload class="avatar-uploader" :http-request="uploadFile" action :on-change="handleChange"
                            :show-file-list="false" :disabled="!!avatar_url">
                            <!-- 如果还没有头像，就展示上传按钮 / 占位符 -->
                            <div v-if="!avatar_url" class="avatar-placeholder">
                                <i class="el-icon-plus avatar-icon"></i>
                            </div>
                        </el-upload>
                        <!-- 自定义的头像容器与删除按钮 -->
                        <div v-if="avatar_url" class="avatar-wrapper">
                            <img :src="avatar_url" class="avatar-img" @click="previewVisible = true" alt="Avatar" />
                            <!-- 悬浮在右上角的 “X” 按钮，用于删除头像 -->
                            <span class="avatar-delete" @click="removeAvatar">×</span>
                        </div>
                    </el-form-item>
                </el-form>
            </el-card>
        </div>
        <!-- 自定义预览 Dialog（手写预览） -->
        <el-dialog :visible.sync="previewVisible" title="Avatar Preview" width="400px" destroy-on-close>
            <img v-if="avatar_url" :src="avatar_url" class="preview-img" alt="Avatar Preview">
        </el-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import VueTelInput from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

export default {        
    name: "ProfileDetails",
    components: {
        VueTelInput,
    },
    data() {
        return {
            form: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                avatar_image: null,
            },
            avatar_url: "", // 用于存储已有或新上传的头像地址
            previewVisible: false, // 控制自定义预览的显示与隐藏
        };
    },
    computed: {
        ...mapGetters("user", ["userData"]),
    },
    watch: {
        userData: {
            immediate: true,
            deep: true,
            handler(newUserData) {
                if (newUserData) {
                    this.form.first_name = newUserData.first_name || "";
                    this.form.last_name = newUserData.last_name || "";
                    this.form.email = newUserData.email || "";
                    this.form.phone = newUserData.phone || "";
                    this.avatar_url = newUserData.avatar_url || "";
                }
            },
        },
    },
    mounted() {
        this.fetchUserData();
    },
    methods: {
        ...mapActions("user", ["updateUserData", "fetchUserData"]),
        async updateUser() {
            // 创建一个副本并清理电话号码中的空格和特殊字符
            const formData = {
                ...this.form,
                phone: this.form.phone.replace(/\s+/g, '').replace(/[^\d+]/g, '')
            };

            const success = await this.updateUserData(formData);
            if (success) {
                this.$message.success("The user information has been updated successfully!");
                this.fetchUserData();
            } else {
                console.log('Update failed. Please try again later!');
            }
        },
        /** Discard Changes */
        resetForm() {
            if (this.userData) {
                this.form.first_name = this.userData.first_name || "";
                this.form.last_name = this.userData.last_name || "";
                this.form.email = this.userData.email || "";
                this.form.phone = this.userData.phone || "";
                this.form.avatar_url = this.userData.avatar_url || "";
            }
        },
        /**
         * 文件上传回调（模拟）：此处只做本地预览
         * 真实项目中应调用后台 API 上传，成功后得到图片的线上地址
         */
        async uploadFile({ file }) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            this.form.avatar_image = file
            reader.onload = (e) => {
                this.avatar_url = e.target.result;
            };
        },

        /**
         * 监听文件选择变化
         */
        handleChange(file, fileList) {
            // 这里可做校验，比如只允许上传图片后缀名等
            console.log("handleChange：", file, fileList);
        },
        /**
         * 取消头像
         */
        removeAvatar() {
            this.avatar_url = "";
        },
    },
};
</script>

<style scoped>
.profile-update-page {
    padding: 20px;
    background-color: #f6f6f6;
    font-family: Arial, sans-serif;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
}

.header h2 {
    font-size: 20px;
    font-weight: bold;
    margin: 0;
    color: #333;
}

.actions {
    display: flex;
    gap: 10px;
}

.content {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    width: 100%;
}

.form-card {
    padding: 20px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
}

/* 头像上传按钮样式 */
.avatar-uploader {
    display: inline-block;
}

/* 如果还没上传头像，显示一个带 + 号的占位符 */
.avatar-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 2px dashed #ccc;
}

.avatar-icon {
    font-size: 24px;
    color: #909399;
}

/* 自定义头像容器 */
.avatar-wrapper {
    position: relative;
    display: inline-block;
}

/* 头像样式 */
.avatar-img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    cursor: pointer;
    border: 2px solid #ddd;
    transition: 0.3s;
}

.avatar-img:hover {
    border-color: #409eff;
}

/* 删除按钮：X */
.avatar-delete {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 20px;
    height: 20px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: #fff;
    background-color: #f56c6c;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* 预览图片 */
.preview-img {
    width: 100%;
    display: block;
    border-radius: 8px;
}
</style>